"use client";

import * as React from "react";

import { InlineTooltip } from "@/components/nk/inline-tooltip";
import type { YearSummary } from "@/lib/simulator/year-aggregator";

/* ──────────────────────────────────────────────────────────────────────────
 * BreakdownTable — year-by-year detail underneath the outcome headline.
 *
 * Five columns: YEAR · LOAN BALANCE · PORTFOLIO · POTENTIAL TAX REFUND · NET.
 *
 * Simplified from the original 7-column spec — INTEREST PAID and DIVIDENDS
 * dropped because in the v1 simulator model both are invisible to the user:
 *   - Interest capitalizes onto the loan balance (HELOC nominal-monthly),
 *     so its effect is already in the LOAN BALANCE column growing year over
 *     year. Showing it separately doubles the visual weight of a number the
 *     user can read off the trend line directly.
 *   - Dividends reinvest via DRIP into more units, so their effect is
 *     already in the PORTFOLIO column. Showing them separately implies a
 *     cash stream the user never sees and was a UX-honesty bug.
 *
 * The POTENTIAL TAX REFUND column carries an inline tooltip on the totals
 * row, matching the /learn Act 2 tally pattern: small blue underline that
 * opens a popover explaining the tracker's role in protecting this refund
 * through the CRA paper-trail discipline.
 *
 * Monospace, hairline rules, no card. Mobile: horizontal scroll inside the
 * table container. Same visual register as the Learn Act 3/6 ledger tables.
 * ────────────────────────────────────────────────────────────────────────── */

const TRACKER_TOOLTIP_BODY =
  "To claim this deduction, the CRA needs a clean paper trail tracing every borrowed dollar to its investment use. The North Kove tracker keeps that record automatically.";

const formatCurrency = (value: number) => {
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  const formatted = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(abs);
  return value < 0 ? `−${formatted}` : formatted;
};

const formatCurrencyNoSign = (value: number) => {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
};

export interface BreakdownTableProps {
  years: YearSummary[];
  /** Whether tax-refund column has meaningful values (income provided). */
  showTaxRefund: boolean;
}

export function BreakdownTable({ years, showTaxRefund }: BreakdownTableProps) {
  if (years.length === 0) return null;

  const lastYear = years[years.length - 1]!;
  const totalTaxRefund = years.reduce((sum, y) => sum + y.taxRefund, 0);

  // Totals row: sum the flow column (tax refund), report year-end values for
  // balance columns and the cumulative NET (which matches the headline).
  const totals = {
    taxRefund: totalTaxRefund,
    loanBalance: lastYear.loanBalance,
    portfolioValue: lastYear.portfolioValue,
    net: lastYear.net,
  };

  return (
    <div className="mt-6">
      <div className="overflow-x-auto">
        <table className="font-mono text-sm tabular-nums">
          <thead>
            <tr className="border-t border-b border-nk-border-strong align-bottom text-xs tracking-[0.15em] text-nk-foreground-subtle uppercase">
              <th scope="col" className="px-3 py-2 text-left font-medium">
                Year
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Loan balance
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Portfolio
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Potential
                <br />
                tax refund
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                Net
              </th>
            </tr>
          </thead>
          <tbody>
            {years.map((y) => (
              <tr key={y.year} className="text-nk-foreground-muted">
                <th scope="row" className="px-3 py-1.5 text-left font-medium">
                  {y.year}
                </th>
                <td className="px-3 py-1.5 text-right">{formatCurrencyNoSign(y.loanBalance)}</td>
                <td className="px-3 py-1.5 text-right">{formatCurrencyNoSign(y.portfolioValue)}</td>
                <td className="px-3 py-1.5 text-right">
                  {showTaxRefund ? formatCurrencyNoSign(y.taxRefund) : "—"}
                </td>
                <td className="px-3 py-1.5 text-right">{formatCurrency(y.net)}</td>
              </tr>
            ))}
            <tr className="border-t border-nk-border-strong font-bold text-nk-foreground">
              <th scope="row" className="px-3 py-2 text-left">
                Total
              </th>
              <td className="px-3 py-2 text-right">{formatCurrencyNoSign(totals.loanBalance)}</td>
              <td className="px-3 py-2 text-right">
                {formatCurrencyNoSign(totals.portfolioValue)}
              </td>
              <td className="px-3 py-2 text-right">
                {showTaxRefund ? (
                  <InlineTooltip
                    ariaLabel="About the potential tax refund total"
                    body={TRACKER_TOOLTIP_BODY}
                  >
                    {formatCurrencyNoSign(totals.taxRefund)}
                  </InlineTooltip>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-2 text-right">{formatCurrency(totals.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-nk-foreground-subtle">
        The chart above shows the same story visually.
      </p>
    </div>
  );
}
